import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Download, CheckCircle2, X, FileSignature, ShieldCheck, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { SignaturePad } from '@/components/SignaturePad';
import { ESignConsent } from '@/components/ESignConsent';
import { API } from '@/lib/api';

function fmt(n) {
    try { return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; } catch { return n; }
}

export default function PublicQuotePage() {
    const { token } = useParams();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [signerName, setSignerName] = useState('');
    const [esignConsented, setEsignConsented] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const padRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            try {
                const r = await axios.get(`${API}/public/quote/${token}`);
                setQuote(r.data);
            } catch (e) {
                setError(e?.response?.data?.detail || 'Quote not found');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const handleSign = async (accepted) => {
        if (accepted) {
            if (!esignConsented) {
                toast.error('Please check the E-SIGN Act consent box before signing.');
                return;
            }
            if (!padRef.current?.hasDrawn()) {
                toast.error('Please draw your signature in the box.');
                return;
            }
            if (!signerName.trim()) {
                toast.error('Please enter your full name.');
                return;
            }
        }
        const signature = padRef.current?.getDataUrl();
        setSubmitting(true);
        try {
            await axios.post(`${API}/public/quote/${token}/sign`, {
                signer_name: signerName || 'Homeowner',
                signature_data_url: signature,
                accepted,
            });
            // Reload
            const r = await axios.get(`${API}/public/quote/${token}`);
            setQuote(r.data);
            toast.success(accepted ? 'Quote signed. Thank you!' : 'Quote declined.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
    if (error) return (
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
            <X className="h-10 w-10 text-red-500" />
            <h2 className="mt-3 text-xl font-display">Quote unavailable</h2>
            <p className="text-slate-600 mt-1">{error}</p>
        </div>
    );
    if (!quote) return null;

    const c = quote.costs || {};
    const items = [
        ['Materials', c.materials], ['Labor', c.labor], ['Tear-off &amp; Disposal', c.disposal],
        ['City Permit', c.permit], ['Overhead &amp; profit', c.other],
    ].filter(([_, v]) => v !== null && v !== undefined && v !== '' && Number(v) > 0);
    const subtotal = items.reduce((s, [_, v]) => s + Number(v || 0), 0);
    const taxRate = Number(c.tax_rate || 0);
    const tax = subtotal * (taxRate / 100);
    const total = subtotal + tax;
    const signed = quote.status === 'signed';
    const declined = quote.status === 'declined';

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Logo />
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Quote {quote.quote_number}
                    </Badge>
                </div>
            </header>
            <div className="max-w-3xl mx-auto p-4 sm:p-8">
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex justify-between items-start gap-4">
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-display tracking-tight text-slate-900" data-testid="public-quote-title">
                                    Your Roofing Quote
                                </h1>
                                <p className="text-sm text-slate-500 mt-1">
                                    Prepared for <b className="text-slate-700">{quote.homeowner_name || 'Homeowner'}</b>
                                </p>
                                <p className="text-sm text-slate-500">{quote.property_address}</p>
                            </div>
                            <Button asChild variant="outline" data-testid="public-quote-download-pdf">
                                <a href={`${API}/public/quote/${token}/pdf`} target="_blank" rel="noopener noreferrer">
                                    <Download className="h-4 w-4 mr-1" /> PDF
                                </a>
                            </Button>
                        </div>
                        {(signed || declined) && (
                            <div className={`mt-4 p-3 rounded-lg ${signed ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                                {signed ? <><CheckCircle2 className="h-4 w-4 inline mr-1" /> This quote was signed by {quote.signature?.signer_name} on {quote.signature?.signed_at?.slice(0,10)}.</> : <><X className="h-4 w-4 inline mr-1" /> This quote was declined.</>}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader>
                        <CardTitle className="font-display text-lg">Contractor</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-1">
                        <div className="font-medium text-slate-900">{quote.company_info?.legal_name || 'Roofing Contractor'}</div>
                        <div>{quote.company_info?.address}</div>
                        <div>{quote.company_info?.phone} · {quote.company_info?.email}</div>
                        <div>License #: {quote.company_info?.license_number} ({quote.company_info?.license_state})</div>
                        <div>Liability Insurance: {quote.company_info?.liability_insurance}</div>
                        <div>Workers Comp: {quote.company_info?.workers_comp}</div>
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Scope of Work</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-1">
                        <div><b>Job:</b> {quote.scope_of_work?.job_type}</div>
                        <div><b>Flashing:</b> {quote.scope_of_work?.flashing}</div>
                        <div><b>Drip Edges:</b> {quote.scope_of_work?.drip_edges}</div>
                        <div><b>Ventilation:</b> {quote.scope_of_work?.ventilation}</div>
                        {quote.scope_of_work?.notes && <div><b>Notes:</b> {quote.scope_of_work.notes}</div>}
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Materials</CardTitle></CardHeader>
                    <CardContent>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-500">
                                    <th className="py-1">Category</th><th>Brand / Spec</th><th className="text-right">Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(quote.materials || []).map((m, i) => (
                                    <tr key={i} className="border-t">
                                        <td className="py-2">{m.category}</td>
                                        <td>{m.brand_type_grade}</td>
                                        <td className="text-right tabular-nums">{m.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Itemized Costs</CardTitle></CardHeader>
                    <CardContent>
                        <table className="w-full text-sm">
                            <tbody>
                                {items.map(([label, v], i) => (
                                    <tr key={i} className="border-t">
                                        <td className="py-2 text-slate-600" dangerouslySetInnerHTML={{ __html: label }} />
                                        <td className="py-2 text-right tabular-nums">{fmt(v)}</td>
                                    </tr>
                                ))}
                                <tr className="border-t font-medium">
                                    <td className="py-2">Subtotal</td><td className="py-2 text-right tabular-nums">{fmt(subtotal)}</td>
                                </tr>
                                {taxRate > 0 && (
                                    <tr className="border-t">
                                        <td className="py-2">Tax ({taxRate}%)</td><td className="py-2 text-right tabular-nums">{fmt(tax)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Homeowner-facing RANGE — protects roofer credibility under uncertainty */}
                        {(() => {
                            const rng = quote.range_estimate || null;
                            const low = rng?.low ?? total * 0.95;
                            const high = rng?.high ?? total * 1.10;
                            const lowPctRaw = rng?.low_pct ?? 0.95;
                            const highPctRaw = rng?.high_pct ?? 1.10;
                            const lowPct = Math.round((1 - lowPctRaw) * 100);
                            const highPct = Math.round((highPctRaw - 1) * 100);
                            if (!total || total <= 0) return null;
                            return (
                                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="public-quote-range">
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div>
                                            <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">
                                                Preliminary Estimate (−{lowPct}% to +{highPct}%)
                                            </div>
                                            <div className="text-2xl sm:text-3xl font-display font-semibold text-slate-900 tabular-nums mt-1" data-testid="public-quote-range-amount">
                                                {fmt(low)} <span className="text-slate-400 font-normal mx-1">–</span> {fmt(high)}
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="bg-white border-amber-300 text-amber-800 font-normal">
                                            Final price confirmed on-site
                                        </Badge>
                                    </div>
                                    <p className="mt-3 text-xs text-slate-600 leading-relaxed">
                                        This range reflects the typical variance for this scope of work. The final price will be confirmed after we
                                        walk the roof together and inspect for hidden damage, decking condition, and any code upgrades. Itemized
                                        costs above show the indicative breakdown.
                                    </p>
                                </div>
                            );
                        })()}
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Decking Replacement Clause</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-700">
                        <div>Per-sheet price for replacement of damaged decking: <b>{fmt(quote.decking_clause?.price_per_sheet)}</b></div>
                        {quote.decking_clause?.notes && <p className="mt-1">{quote.decking_clause.notes}</p>}
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Timeline</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-1">
                        <div><b>Start:</b> {quote.timeline?.start_date || 'TBD'}</div>
                        <div><b>Completion:</b> {quote.timeline?.end_date || 'TBD'}</div>
                        <div><b>Daily Cleanup:</b> {quote.timeline?.daily_cleanup}</div>
                        <div><b>Final Walkthrough:</b> {quote.timeline?.final_walkthrough}</div>
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg">Payment Terms</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-1">
                        <div><b>Deposit:</b> {quote.payment_terms?.deposit}</div>
                        <div><b>Progress:</b> {quote.payment_terms?.progress}</div>
                        <div><b>Final:</b> {quote.payment_terms?.final}</div>
                        <div><b>Methods:</b> {quote.payment_terms?.methods}</div>
                    </CardContent>
                </Card>

                <Card className="mt-4">
                    <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Warranties</CardTitle></CardHeader>
                    <CardContent className="text-sm text-slate-700 space-y-1">
                        <div><b>Manufacturer:</b> {quote.warranties?.manufacturer}</div>
                        <div><b>Workmanship:</b> {quote.warranties?.workmanship}</div>
                    </CardContent>
                </Card>

                {!signed && !declined && (
                    <Card className="mt-6 border-amber-200">
                        <CardHeader><CardTitle className="font-display text-lg flex items-center gap-2"><FileSignature className="h-5 w-5 text-amber-600" /> Accept &amp; Sign</CardTitle></CardHeader>
                        <CardContent>
                            <ESignConsent
                                consented={esignConsented}
                                onChange={setEsignConsented}
                                quoteNumber={quote.quote_number}
                                contractorEmail={quote.company_info?.email}
                            />
                            <div className="mt-4">
                                <Label htmlFor="signer_name">Your full name</Label>
                                <Input id="signer_name" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Jane Smith" data-testid="public-quote-signer-name" />
                            </div>
                            <div className="mt-4">
                                <Label>Signature</Label>
                                <SignaturePad ref={padRef} />
                            </div>
                            <div className="mt-4 flex flex-col sm:flex-row gap-2">
                                <Button onClick={() => handleSign(true)} disabled={submitting || !esignConsented} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-11" data-testid="quote-accept-button">
                                    {submitting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Accept &amp; Sign
                                </Button>
                                <Button onClick={() => handleSign(false)} disabled={submitting} variant="outline" className="flex-1 h-11" data-testid="quote-decline-button">
                                    <X className="h-4 w-4 mr-1" /> Decline
                                </Button>
                            </div>
                            <p className="text-xs text-slate-500 mt-3">By signing electronically, you agree to the terms outlined in this quote. A signed PDF will be sent to you and the contractor.</p>
                        </CardContent>
                    </Card>
                )}
            </div>
            <footer className="py-6 text-center text-xs text-slate-500">Powered by QuotesFlow AI Roofing</footer>
        </div>
    );
}
